import type { SuggestionsResponse } from '../types';
import { formatCurrency, formatDateKey } from '../utils/format';

interface Props {
  data: SuggestionsResponse | null;
  loading?: boolean;
}

export function InsightsPanel({ data, loading = false }: Props) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card h-48 animate-pulse bg-slate-800/40" />
        <div className="card h-48 animate-pulse bg-slate-800/40" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="card text-sm text-slate-400">
        No AI insights generated yet. Ingest a document (PDF → .md via Docling, or a .md file
        directly) and the analysis will appear here.
      </div>
    );
  }

  const spendingTraps = data.spending_traps ?? [];
  const investmentSuggestions = data.investment_suggestions ?? [];

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {/* Card 1 — Spending traps */}
      <div className="card border-rose-500/20">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-bold text-slate-100">🚨 High-Priority Spending Leaks & Traps</h3>
          <span className="rounded-full bg-rose-500/15 px-2.5 py-1 text-xs font-semibold text-rose-300">
            {spendingTraps.length}
          </span>
        </div>
        {spendingTraps.length > 0 ? (
          <ul className="space-y-2">
            {spendingTraps.map((item, i) => (
              <li
                key={i}
                className="flex gap-2.5 rounded-lg bg-slate-800/40 px-3 py-2.5 text-sm text-slate-300"
              >
                <span className="mt-0.5 shrink-0 text-rose-400">•</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">No spending traps identified for the latest period.</p>
        )}
        <div className="mt-4 rounded-lg bg-slate-800/40 px-3 py-2 text-xs text-slate-500">
          Period: {formatDateKey(data.period_key ?? '')} · Spent {formatCurrency(data.total_spent ?? 0)} ·
          Income {formatCurrency(data.total_income ?? 0)}
        </div>
      </div>

      {/* Card 2 — Investment recommendations */}
      <div className="card border-emerald-500/20">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-bold text-slate-100">📈 Actionable Investment Recommendations</h3>
          <span className="rounded-full bg-emerald-500/15 px-2.5 py-1 text-xs font-semibold text-emerald-300">
            {investmentSuggestions.length}
          </span>
        </div>
        {investmentSuggestions.length > 0 ? (
          <ul className="space-y-2">
            {investmentSuggestions.map((item, i) => (
              <li
                key={i}
                className="flex gap-2.5 rounded-lg bg-slate-800/40 px-3 py-2.5 text-sm text-slate-300"
              >
                <span className="mt-0.5 shrink-0 text-emerald-400">📊</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">No investment suggestions for the latest period yet.</p>
        )}
      </div>
    </div>
  );
}
